import React, { FormEvent, useEffect, useState } from 'react';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ShieldGPSAxiosClient from '../lib/ShieldGPSAxiosClient';
import { User } from '../models/User';

const MIN_SUGGESTION_QUERY_LENGTH = 3;
const SUGGESTION_DEBOUNCE_MS = 250;

const SearchForm = styled.form`
  width: 100%;
  max-width: 440px;
`;

export enum SearchQueryType {
  UNKNOWN_SEARCH,
  IMEI_SEARCH,
  ICCID_SEARCH,
  EMAIL_SEARCH,
}

export interface UserSearchSuggestion {
  type: 'user';
  id: number;
  matchedField: 'email';
  email: string;
  firstName: string;
  lastName: string;
}

export interface DeviceSearchSuggestion {
  type: 'device';
  id: number;
  matchedField: 'imei' | 'iccid';
  imei: string;
  iccid: string | null;
  ownerEmail: string | null;
}

export type SearchSuggestion = UserSearchSuggestion | DeviceSearchSuggestion;

interface SearchSuggestionsResponse {
  suggestions: SearchSuggestion[];
}

interface SearchUserResult {
  matches: User[];
}

export const determineSearchQueryType = (query: string): SearchQueryType => {
  const emailRegExp: RegExp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const imeiRegExp: RegExp = /^\d{15}$/;
  const iccidRegExp: RegExp = /^\d{19}$/;

  if (emailRegExp.test(query)) {
    return SearchQueryType.EMAIL_SEARCH;
  }
  if (imeiRegExp.test(query)) {
    return SearchQueryType.IMEI_SEARCH;
  }
  if (iccidRegExp.test(query)) {
    return SearchQueryType.ICCID_SEARCH;
  }
  return SearchQueryType.UNKNOWN_SEARCH;
};

const searchForUser = async (searchField: string, searchValue: string): Promise<SearchUserResult> => {
  const response = await ShieldGPSAxiosClient.post<SearchUserResult>('/admin/user-search', {
    searchField,
    searchValue,
  });
  return response.data;
};

const getSearchSuggestions = async (query: string): Promise<SearchSuggestionsResponse> => {
  const response = await ShieldGPSAxiosClient.post<SearchSuggestionsResponse>(
    '/admin/user-search/suggestions',
    { query },
  );
  return response.data;
};

const useDebouncedValue = (value: string, delay: number): string => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timeout);
  }, [delay, value]);

  return debouncedValue;
};

const getSuggestionValue = (suggestion: SearchSuggestion): string => {
  if (suggestion.type === 'user') {
    return suggestion.email;
  }
  if (suggestion.matchedField === 'iccid' && suggestion.iccid) {
    return suggestion.iccid;
  }
  return suggestion.imei;
};

export const getSuggestionPath = (suggestion: SearchSuggestion): string => {
  if (suggestion.type === 'user') {
    return `/users/${suggestion.id}`;
  }
  return `/devices/${suggestion.imei}`;
};

export const highlightSuggestionMatches = (text: string, query: string): React.ReactNode => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return text;
  }

  const normalizedText = text.toLowerCase();
  const parts: React.ReactNode[] = [];
  let position = 0;
  let matchPosition = normalizedText.indexOf(normalizedQuery, position);

  while (matchPosition !== -1) {
    if (matchPosition > position) {
      parts.push(text.slice(position, matchPosition));
    }
    parts.push(
      <Box component="strong" key={`${matchPosition}-${parts.length}`} sx={{ fontWeight: 700 }}>
        {text.slice(matchPosition, matchPosition + normalizedQuery.length)}
      </Box>,
    );
    position = matchPosition + normalizedQuery.length;
    matchPosition = normalizedText.indexOf(normalizedQuery, position);
  }

  if (position === 0) {
    return text;
  }
  if (position < text.length) {
    parts.push(text.slice(position));
  }

  return parts;
};

const NavbarSearch = () => {
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();
  const normalizedSearchValue = searchValue.trim().toLowerCase();
  const debouncedSearchValue = useDebouncedValue(normalizedSearchValue, SUGGESTION_DEBOUNCE_MS);
  const suggestionsEnabled = debouncedSearchValue.length >= MIN_SUGGESTION_QUERY_LENGTH;
  const isWaitingForSuggestions = normalizedSearchValue.length >= MIN_SUGGESTION_QUERY_LENGTH
    && debouncedSearchValue !== normalizedSearchValue;

  const suggestionsQuery = useQuery<SearchSuggestionsResponse, Error>(
    ['admin-search-suggestions', debouncedSearchValue],
    () => getSearchSuggestions(debouncedSearchValue),
    {
      enabled: suggestionsEnabled,
      retry: false,
      staleTime: 30_000,
    },
  );

  const suggestions = suggestionsEnabled && debouncedSearchValue === normalizedSearchValue
    ? suggestionsQuery.data?.suggestions || []
    : [];
  const suggestionsLoading = isWaitingForSuggestions || suggestionsQuery.isFetching;

  const navigateToSuggestion = (suggestion: SearchSuggestion) => {
    setSearchValue(getSuggestionValue(suggestion));
    navigate(getSuggestionPath(suggestion));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const queryType = determineSearchQueryType(normalizedSearchValue);
    if (queryType === SearchQueryType.IMEI_SEARCH) {
      navigate(`/devices/${normalizedSearchValue}`);
    } else if (queryType === SearchQueryType.ICCID_SEARCH) {
      navigate(`/devices/${normalizedSearchValue}?id_type=iccid`);
    } else if (queryType === SearchQueryType.EMAIL_SEARCH) {
      searchForUser('email', normalizedSearchValue)
        .then((result: SearchUserResult) => {
          if (result.matches.length === 0) {
            alert(`Couldn't find any matches for ${normalizedSearchValue}`);
            return;
          }

          navigate(`/users/${result.matches[0].id}`);
        })
        .catch((error) => {
          console.error(error);
          alert(`Failed to lookup user ${normalizedSearchValue}`);
        });
    } else {
      alert('Enter a complete email, IMEI, or ICCID, or choose a suggestion.');
    }
  };

  return (
    <SearchForm onSubmit={handleSubmit}>
      <Autocomplete<SearchSuggestion, false, false, true>
        freeSolo
        filterOptions={(options) => options}
        getOptionLabel={(option) => typeof option === 'string' ? option : getSuggestionValue(option)}
        inputValue={searchValue}
        loading={suggestionsLoading}
        noOptionsText={normalizedSearchValue.length < MIN_SUGGESTION_QUERY_LENGTH
          ? 'Type at least 3 characters'
          : suggestionsQuery.isError ? 'Suggestions unavailable' : 'No matches'}
        onChange={(_event, suggestion) => {
          if (suggestion && typeof suggestion !== 'string') {
            navigateToSuggestion(suggestion);
          }
        }}
        onInputChange={(_event, value, reason) => {
          if (reason === 'input' || reason === 'clear') {
            setSearchValue(value);
          }
        }}
        options={suggestions}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Search users and devices',
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {suggestionsLoading ? (
                    <CircularProgress color="inherit" size={18} sx={{ mr: 4 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            placeholder="Search by email, IMEI, or ICCID…"
            size="small"
          />
        )}
        renderOption={(props, suggestion) => {
          const resultType = suggestion.type === 'user' ? 'User' : 'Device';
          const primaryText = suggestion.type === 'user'
            ? suggestion.email
            : `${suggestion.matchedField.toUpperCase()} ${getSuggestionValue(suggestion)}`;
          const secondaryText = suggestion.type === 'user'
            ? [resultType, [suggestion.firstName, suggestion.lastName].filter(Boolean).join(' ')]
              .filter(Boolean).join(' · ')
            : [
              resultType,
              suggestion.matchedField === 'iccid' ? `IMEI ${suggestion.imei}` : null,
              suggestion.ownerEmail,
            ].filter(Boolean).join(' · ');

          return (
            <Box component="li" {...props} key={`${suggestion.type}-${suggestion.id}-${suggestion.matchedField}`}>
              <Box
                aria-label={`${resultType} result`}
                role="img"
                sx={{
                  alignItems: 'center',
                  backgroundColor: suggestion.type === 'user' ? 'transparent' : 'action.hover',
                  borderRadius: '50%',
                  color: suggestion.type === 'user' ? 'primary.main' : 'text.secondary',
                  display: 'flex',
                  flexShrink: 0,
                  height: 32,
                  justifyContent: 'center',
                  mr: 1.5,
                  width: 32,
                }}
              >
                {suggestion.type === 'user'
                  ? <PersonOutlineIcon fontSize="small" />
                  : <GpsFixedIcon fontSize="small" />}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap variant="body2">
                  {highlightSuggestionMatches(primaryText, normalizedSearchValue)}
                </Typography>
                {secondaryText ? (
                  <Typography color="text.secondary" noWrap variant="caption">
                    {highlightSuggestionMatches(secondaryText, normalizedSearchValue)}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        }}
        sx={{
          width: '100%',
          '& .MuiInputBase-root': {
            backgroundColor: 'white',
            fontSize: '16px',
          },
        }}
      />
    </SearchForm>
  );
};

export default NavbarSearch;
